import { gql } from '@urql/core';

const AUTOMATION_FRAGMENT = gql`
  fragment AutomationDetail on Automation {
    id
    createdAt
    updatedAt
    code
    name
    description
    isEnabled
    priority
    triggerType
    triggerConfig
    templateCode
    totalExecutions
    successfulExecutions
    failedExecutions
    lastExecutedAt
    steps {
      id
      stepOrder
      type
      actionType
      config
    }
    translations {
      id
      languageCode
      name
      description
    }
  }
`;

export const GET_AUTOMATIONS = gql`
  query GetAutomations($options: AutomationListOptions) {
    automations(options: $options) {
      items {
        ...AutomationDetail
      }
      totalItems
    }
  }
  ${AUTOMATION_FRAGMENT}
`;

export const GET_AUTOMATION = gql`
  query GetAutomation($id: ID!) {
    automation(id: $id) {
      ...AutomationDetail
    }
  }
  ${AUTOMATION_FRAGMENT}
`;

export const CREATE_AUTOMATION = gql`
  mutation CreateAutomation($input: CreateAutomationInput!) {
    createAutomation(input: $input) {
      ...AutomationDetail
    }
  }
  ${AUTOMATION_FRAGMENT}
`;

export const UPDATE_AUTOMATION = gql`
  mutation UpdateAutomation($input: UpdateAutomationInput!) {
    updateAutomation(input: $input) {
      ...AutomationDetail
    }
  }
  ${AUTOMATION_FRAGMENT}
`;

export const TOGGLE_AUTOMATION = gql`
  mutation ToggleAutomation($id: ID!, $isEnabled: Boolean!) {
    toggleAutomation(id: $id, isEnabled: $isEnabled) {
      id
      isEnabled
    }
  }
`;

export const CREATE_AUTOMATION_FROM_TEMPLATE = gql`
  mutation CreateAutomationFromTemplate(
    $templateCode: String!
    $input: CreateAutomationFromTemplateInput
  ) {
    createAutomationFromTemplate(templateCode: $templateCode, input: $input) {
      ...AutomationDetail
    }
  }
  ${AUTOMATION_FRAGMENT}
`;

export const GET_AUTOMATION_EXECUTIONS = gql`
  query GetAutomationExecutions(
    $automationId: ID
    $options: AutomationExecutionListOptions
  ) {
    automationExecutions(automationId: $automationId, options: $options) {
      items {
        id
        createdAt
        automationId
        triggerEntityType
        triggerEntityId
        status
        currentStepOrder
        startedAt
        completedAt
        error
        logs {
          id
          stepOrder
          actionType
          status
          result
          error
          executedAt
          durationMs
        }
      }
      totalItems
    }
  }
`;

export const CANCEL_AUTOMATION_EXECUTION = gql`
  mutation CancelAutomationExecution($executionId: ID!) {
    cancelAutomationExecution(executionId: $executionId) {
      id
      status
    }
  }
`;

export const TEST_AUTOMATION = gql`
  mutation TestAutomation($automationId: ID!, $entityId: ID!) {
    testAutomation(automationId: $automationId, entityId: $entityId) {
      id
      status
    }
  }
`;

export const GET_AUTOMATION_TRIGGER_DEFINITIONS = gql`
  query GetAutomationTriggerDefinitions {
    automationTriggerDefinitions {
      code
      description
      entityType
      category
      source
      args { name type label description required defaultValue ui }
    }
  }
`;

export const GET_AUTOMATION_CONDITION_DEFINITIONS = gql`
  query GetAutomationConditionDefinitions {
    automationConditionDefinitions {
      code
      description
      category
      args { name type label description required defaultValue ui }
    }
  }
`;

export const GET_AUTOMATION_ACTION_DEFINITIONS = gql`
  query GetAutomationActionDefinitions {
    automationActionDefinitions {
      code
      description
      category
      args { name type label description required defaultValue ui }
    }
  }
`;

export const GET_AUTOMATION_TEMPLATES = gql`
  query GetAutomationTemplates {
    automationTemplates {
      code
      name
      description
      triggerType
      triggerConfig
      steps { type actionType config }
    }
  }
`;

export const GET_AUTOMATION_SECRETS = gql`
  query GetAutomationSecrets {
    automationSecrets {
      id
      key
      description
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_AUTOMATION_SECRET = gql`
  mutation CreateAutomationSecret($input: CreateAutomationSecretInput!) {
    createAutomationSecret(input: $input) {
      id
      key
      description
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_AUTOMATION_SECRET = gql`
  mutation UpdateAutomationSecret($id: ID!, $input: UpdateAutomationSecretInput!) {
    updateAutomationSecret(id: $id, input: $input) {
      id
      key
      description
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_AUTOMATION_SECRET = gql`
  mutation DeleteAutomationSecret($id: ID!) {
    deleteAutomationSecret(id: $id) {
      result
      message
    }
  }
`;

export const GET_AUTOMATION_VARIABLE_DEFINITIONS = gql`
  query GetAutomationVariableDefinitions($triggerType: String!) {
    automationVariableDefinitions(triggerType: $triggerType) {
      path
      type
      label
      description
    }
  }
`;
